'use client';

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { useCursorEnabled } from '../cursor/useCursorEnabled';

// Full-bleed radial gradient that follows the pointer at ~5% gold alpha.
// Connective tissue between the sharp cursor and the still pond — the
// background reads as lit by the visitor's attention.
//
// Single fixed div whose background-image references two CSS custom
// properties; updates are GPU-composited. Lerp at 0.12 for a calm follow.
// Skipped on /admin, on touch, and on reduced-motion.
export function CursorSpotlight() {
  const enabled = useCursorEnabled();
  const pathname = usePathname();
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!enabled) return;
    if (pathname?.startsWith('/admin')) return;
    const el = ref.current;
    if (!el) return;

    let x = window.innerWidth / 2;
    let y = window.innerHeight / 2;
    let targetX = x;
    let targetY = y;
    let rafQueued = false;

    function tick() {
      rafQueued = false;
      x += (targetX - x) * 0.12;
      y += (targetY - y) * 0.12;
      if (!el) return;
      el.style.setProperty('--spotlight-x', `${x.toFixed(0)}px`);
      el.style.setProperty('--spotlight-y', `${y.toFixed(0)}px`);
      if (Math.abs(x - targetX) > 0.5 || Math.abs(y - targetY) > 0.5) {
        rafQueued = true;
        requestAnimationFrame(tick);
      }
    }

    function onMove(e: PointerEvent) {
      targetX = e.clientX;
      targetY = e.clientY;
      if (!rafQueued) {
        rafQueued = true;
        requestAnimationFrame(tick);
      }
    }

    window.addEventListener('pointermove', onMove, { passive: true });
    return () => window.removeEventListener('pointermove', onMove);
  }, [enabled, pathname]);

  if (!enabled) return null;
  if (pathname?.startsWith('/admin')) return null;

  return (
    <div
      ref={ref}
      aria-hidden="true"
      style={{
        position: 'fixed',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 0,
        backgroundImage:
          'radial-gradient(circle 420px at var(--spotlight-x, 50%) var(--spotlight-y, 50%), color-mix(in srgb, var(--accent) 8%, transparent) 0%, transparent 70%)',
      }}
    />
  );
}
